import { useQuery } from '@tanstack/react-query';

export interface HeyReachHealth {
  status: 'ok' | 'warning' | 'error' | 'not_connected';
  circuitBreaker: 'closed' | 'open' | 'half_open';
  lastWebhookAt: string | null;
  lastSendAt: string | null;
  stats24h: {
    webhooks: { total: number; failed: number; errorRate: number };
    sends: { total: number; failed: number; errorRate: number };
  };
  latency24h: {
    webhookP50Ms: number | null;
    webhookP95Ms: number | null;
    sendP50Ms: number | null;
    sendP95Ms: number | null;
  };
  impact24h: {
    affectedDrafts: number;
    affectedContacts: number;
  };
  trend7d: Array<{
    date: string;
    webhooksOk: number;
    webhooksFail: number;
    sendsOk: number;
    sendsFail: number;
  }>;
  note?: string; // Present when health tracking is not fully enabled
}

interface HealthResponse {
  status: 'healthy' | 'degraded' | 'critical';
  checks: {
    heyreach?: {
      status: 'ok' | 'warning' | 'error' | 'not_connected';
      circuit_breaker?: string;
      last_webhook_at?: string | null;
      last_send_at?: string | null;
      stats_24h?: {
        webhooks: { total: number; failed: number; error_rate: number };
        sends: { total: number; failed: number; error_rate: number };
      };
      latency_24h?: {
        webhook_p50_ms: number | null;
        webhook_p95_ms: number | null;
        send_p50_ms: number | null;
        send_p95_ms: number | null;
      };
      impact_24h?: {
        affected_drafts: number;
        affected_contacts: number;
      };
      trend_7d?: Array<{
        date: string;
        webhooksOk: number;
        webhooksFail: number;
        sendsOk: number;
        sendsFail: number;
      }>;
      note?: string;
      error?: string;
    };
  };
}

// Adaptive polling: faster when degraded.
// `not_connected` cannot self-heal by retrying — it needs a human to
// reconnect the integration in Settings — so polling every 10s forever
// would just be 8,640 wasted requests a day. Stop polling entirely; the
// user can still pull-to-refresh or reload.
// Exported so the stop-polling decision is unit-testable without driving a
// full TanStack Query timer loop through renderHook.
export function getIntegrationHealthRefetchInterval(
  status: HeyReachHealth['status'] | undefined
): number | false {
  if (status === 'not_connected') return false;
  if (status === 'error') return 10_000; // 10s when critical
  if (status === 'warning') return 30_000; // 30s when warning
  return 60_000; // 60s when healthy
}

export function useIntegrationHealth() {
  return useQuery({
    queryKey: ['integration-health'],
    queryFn: async (): Promise<HeyReachHealth | null> => {
      const response = await fetch('/api/os/health');
      
      if (!response.ok) {
        throw new Error('Failed to fetch health status');
      }
      
      const data: HealthResponse = await response.json();
      const heyreach = data.checks.heyreach;
      
      if (!heyreach || heyreach.error) {
        // No HeyReach check or error - don't show
        return null;
      }
      
      // If we have a note (health tracking disabled, or the integration is not
      // connected), show a simplified status rather than fabricating stats.
      if (heyreach.note && (heyreach.status === 'ok' || heyreach.status === 'not_connected')) {
        return {
          status: heyreach.status,
          circuitBreaker: 'closed' as const,
          lastWebhookAt: null,
          lastSendAt: null,
          stats24h: {
            webhooks: { total: 0, failed: 0, errorRate: 0 },
            sends: { total: 0, failed: 0, errorRate: 0 },
          },
          latency24h: {
            webhookP50Ms: null,
            webhookP95Ms: null,
            sendP50Ms: null,
            sendP95Ms: null,
          },
          impact24h: {
            affectedDrafts: 0,
            affectedContacts: 0,
          },
          trend7d: [],
          note: heyreach.note, // Pass note for UI to show
        };
      }
      
      return {
        status: heyreach.status,
        circuitBreaker: (heyreach.circuit_breaker as 'closed' | 'open' | 'half_open') || 'closed',
        lastWebhookAt: heyreach.last_webhook_at || null,
        lastSendAt: heyreach.last_send_at || null,
        stats24h: {
          webhooks: {
            total: heyreach.stats_24h?.webhooks.total || 0,
            failed: heyreach.stats_24h?.webhooks.failed || 0,
            errorRate: heyreach.stats_24h?.webhooks.error_rate || 0,
          },
          sends: {
            total: heyreach.stats_24h?.sends.total || 0,
            failed: heyreach.stats_24h?.sends.failed || 0,
            errorRate: heyreach.stats_24h?.sends.error_rate || 0,
          },
        },
        latency24h: {
          webhookP50Ms: heyreach.latency_24h?.webhook_p50_ms ?? null,
          webhookP95Ms: heyreach.latency_24h?.webhook_p95_ms ?? null,
          sendP50Ms: heyreach.latency_24h?.send_p50_ms ?? null,
          sendP95Ms: heyreach.latency_24h?.send_p95_ms ?? null,
        },
        impact24h: {
          affectedDrafts: heyreach.impact_24h?.affected_drafts || 0,
          affectedContacts: heyreach.impact_24h?.affected_contacts || 0,
        },
        trend7d: heyreach.trend_7d || [],
      };
    },
    refetchInterval: (query) => getIntegrationHealthRefetchInterval(query.state.data?.status),
    staleTime: 10_000,
    retry: 2,
  });
}
